import { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  isAdditionalSupportEnabled,
  isSupportPopupEnabled,
  getSupportFormMode,
} from '../../app/helpers/semanticSearchConfigHelper';
import { startRegisterSupportRequest } from '../../app/actions/userInterestActions';
import { fetchToken } from '../../app/helpers/fetch';
import { const_routes } from '../../app/helpers/const_routes';
import {
  buildAnswersPayload,
  getStaticSupportQuestion,
  getSupportFormQuestions,
} from './supportFormCatalog';
import SupportRequestForm from './SupportRequestForm';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '92%',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  overlay: { backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 3200 },
};

const AdditionalSupportPanel = ({
  moduleKey,
  referenceId = null,
  sourceModule = null,
  originIds = null,
  bridgeKind = null,
  searchinfoId = null,
  searchText = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configuration = useSelector((state) => state.configuration);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [dynamicQuestions, setDynamicQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({
    contact_name: '',
    channel: 'email',
    channel_value: '',
    main_interest_code: '',
    comments: '',
    consent: false,
  });

  const supportOn = isAdditionalSupportEnabled(configuration, moduleKey);
  const popupOn = isSupportPopupEnabled(configuration, moduleKey);
  const formMode = getSupportFormMode(configuration);
  const staticQ = getStaticSupportQuestion(moduleKey);
  const hybridQuestions = useMemo(() => getSupportFormQuestions(moduleKey), [moduleKey]);
  const questions = formMode === 'dynamic' && dynamicQuestions ? dynamicQuestions : hybridQuestions;
  const useCatalog = formMode === 'hybrid' || formMode === 'dynamic';

  const channelLabel = useMemo(() => {
    if (form.channel === 'whatsapp') {
      return t('semanticSearch.support.channelWhatsapp', { defaultValue: 'WhatsApp' });
    }
    if (form.channel === 'phone' || form.channel === 'call') {
      return t('semanticSearch.support.channelPhone', { defaultValue: 'Teléfono' });
    }
    return t('semanticSearch.support.channelEmail', { defaultValue: 'Correo' });
  }, [form.channel, t]);

  useEffect(() => {
    if (!open || formMode !== 'dynamic' || !moduleKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchToken(
          `${const_routes.SCHEMA}`,
          `${const_routes.VERSIONV1}`,
          `${const_routes.ROUTE_USER_INTEREST}`,
          `support-form/${moduleKey}`
        );
        const body = await resp.json();
        if (!cancelled && body.ok) setDynamicQuestions(body.questions || []);
      } catch (_e) {
        if (!cancelled) setDynamicQuestions(hybridQuestions);
      }
    })();
    return () => { cancelled = true; };
  }, [open, formMode, moduleKey, hybridQuestions]);

  if (!supportOn) return null;

  const onCta = async () => {
    if (!popupOn) {
      setSending(true);
      const resp = await dispatch(
        startRegisterSupportRequest({
          module_key: moduleKey,
          reference_id: referenceId,
          sourceModule,
          originIds,
          bridge_kind: bridgeKind,
          searchinfo_id: searchinfoId,
          searchText,
          consent: true,
          form_mode: formMode,
        })
      );
      setSending(false);
      Swal.fire(
        t('semanticSearch.support.title', { defaultValue: 'Soporte' }),
        resp?.msg || t('semanticSearch.support.sent', {
          defaultValue: 'Se ha enviado tu requerimiento y te contactaremos.',
        }),
        resp?.ok ? 'success' : 'error'
      );
      return;
    }
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      Swal.fire(
        t('titles.alert', { defaultValue: 'Alerta' }),
        t('semanticSearch.support.consentRequired', {
          defaultValue: 'Debes autorizar el contacto para continuar.',
        }),
        'warning'
      );
      return;
    }

    const catalogAnswers = useCatalog ? buildAnswersPayload(questions, answers, t) : [];
    if (useCatalog) {
      const missing = questions.find((q) => {
        if (!q.required) return false;
        const val = answers[q.key];
        if (Array.isArray(val)) return val.length === 0;
        return val == null || String(val).trim() === '';
      });
      if (missing) {
        Swal.fire(
          t('titles.alert', { defaultValue: 'Alerta' }),
          t('semanticSearch.form.required', { defaultValue: 'Complete las preguntas obligatorias.' }),
          'warning'
        );
        return;
      }
    }

    setSending(true);
    const first = catalogAnswers[0];
    const commentAns = catalogAnswers.find((a) => a.key === 'comment');
    const comments = useCatalog
      ? (commentAns && String(commentAns.value || '').trim()) || form.comments || null
      : form.comments;
    const staticLabel = t(staticQ.labelKey, { defaultValue: staticQ.label });
    const resp = await dispatch(
      startRegisterSupportRequest({
        module_key: moduleKey,
        reference_id: referenceId,
        sourceModule,
        originIds,
        bridge_kind: bridgeKind,
        searchinfo_id: searchinfoId,
        searchText,
        ...form,
        comments,
        form_mode: formMode,
        answers: catalogAnswers,
        main_interest_code: useCatalog
          ? (Array.isArray(first?.value) ? first.value.join('|') : first?.value)
          : form.main_interest_code,
        payload_json: {
          form_mode: formMode,
          answers: catalogAnswers,
          interest_question: useCatalog ? first?.label : staticLabel,
          originIds,
          bridge_kind: bridgeKind,
          searchinfo_id: searchinfoId,
        },
      })
    );
    setSending(false);
    if (resp?.ok) {
      setOpen(false);
      setAnswers({});
      Swal.fire(
        t('titles.success', { defaultValue: 'Éxito' }),
        resp.msg || t('semanticSearch.support.sent', {
          defaultValue: 'Se ha enviado tu requerimiento y te contactaremos.',
        }),
        'success'
      );
    } else {
      Swal.fire(
        t('titles.error', { defaultValue: 'Error' }),
        resp?.msg || t('semanticSearch.support.sentError', {
          defaultValue: 'No se pudo enviar la solicitud',
        }),
        'error'
      );
    }
  };

  return (
    <div className="additional-support-panel my-3 text-center">
      <button
        type="button"
        className="btn btn-outline-success"
        disabled={sending}
        onClick={onCta}
      >
        {t('semanticSearch.support.cta', {
          defaultValue: '¿Deseas obtener soporte adicional?',
        })}
      </button>

      <Modal
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        style={modalStyles}
        contentLabel={t('semanticSearch.support.formTitle', { defaultValue: 'Soporte adicional' })}
      >
        <h5 className="mb-3">
          {t('semanticSearch.support.formTitle', { defaultValue: 'Soporte adicional' })}
        </h5>
        <p className="small text-muted">
          {t('semanticSearch.support.privacy', {
            defaultValue: 'Al completar estos datos nos autorizas a contactarte. Tu información personal será tratada con confidencialidad.',
          })}
        </p>
        <form onSubmit={onSubmit}>
          <div className="mb-2">
            <label className="form-label">
              {t('semanticSearch.support.name', { defaultValue: 'Nombre' })}
            </label>
            <input
              className="form-control"
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              required
            />
          </div>
          <div className="mb-2">
            <label className="form-label">
              {t('semanticSearch.support.channel', { defaultValue: 'Canal de contacto preferido' })}
            </label>
            <select
              className="form-select"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value, channel_value: '' })}
            >
              <option value="email">{t('semanticSearch.support.channelEmail', { defaultValue: 'Correo' })}</option>
              <option value="phone">{t('semanticSearch.support.channelPhone', { defaultValue: 'Teléfono' })}</option>
              <option value="call">{t('semanticSearch.support.channelCall', { defaultValue: 'Llamada' })}</option>
              <option value="whatsapp">{t('semanticSearch.support.channelWhatsapp', { defaultValue: 'WhatsApp' })}</option>
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label">{channelLabel}</label>
            <input
              className="form-control"
              value={form.channel_value}
              onChange={(e) => setForm({ ...form, channel_value: e.target.value })}
              required
            />
          </div>

          {useCatalog ? (
            <SupportRequestForm
              questions={questions}
              answers={answers}
              onChange={setAnswers}
            />
          ) : (
            <>
              <div className="mb-2">
                <label className="form-label">
                  {t(staticQ.labelKey, { defaultValue: staticQ.label })}
                </label>
                <input
                  className="form-control"
                  value={form.main_interest_code}
                  onChange={(e) => setForm({ ...form, main_interest_code: e.target.value })}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="form-label">
                  {t('semanticSearch.support.comments', { defaultValue: 'Comentarios o dudas específicas' })}
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={form.comments}
                  onChange={(e) => setForm({ ...form, comments: e.target.value })}
                />
              </div>
            </>
          )}

          <div className="form-check mb-3">
            <input
              id="support-consent"
              type="checkbox"
              className="form-check-input"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="support-consent">
              {t('semanticSearch.support.consent', {
                defaultValue: 'Autorizo el contacto y el tratamiento confidencial de mis datos.',
              })}
            </label>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-light" onClick={() => setOpen(false)}>
              {t('titles.cancel', { defaultValue: 'Cancelar' })}
            </button>
            <button type="submit" className="btn btn-success" disabled={sending}>
              {t('titles.acept', { defaultValue: 'Aceptar' })}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdditionalSupportPanel;
